import { getUnprocessableErrors } from '@10xlabs/utils'
import { ApolloError } from '@apollo/client'
import { subMonths } from 'date-fns'
import { Formik, FormikHelpers, getIn, useFormikContext } from 'formik'
import { isEmpty } from 'lodash'
import { ReactElement } from 'react'
import { defineMessages, FormattedMessage, useIntl } from 'react-intl'

import { Button } from 'components/v2/Button'
import { Column } from 'components/v2/Column'
import { DowSelector } from 'components/v2/DowSelector'
import { ErrorMessage } from 'components/v2/ErrorMessage'
import FormLayout from 'components/v2/FormLayout'
import Heading from 'components/v2/Heading'
import { InputDate } from 'components/v2/InputDate'
import { Label } from 'components/v2/Label'
import { Row } from 'components/v2/Row'
import { INewBusinessClientPriceFormValues } from 'features/BusinessClientPrice/types'
import { BusinessClientAutocomplete } from 'features/BusinessClients/components/BusinessClientAutocomplete'
import { BusinessSiteAutocomplete } from 'features/BusinessSites/components/BusinessSiteAutocomplete'
import { RouteAutocomplete } from 'features/Routes/components/RouteAutocomplete'
import { MultiRouteServiceAutocomplete } from 'features/Services/components/MultiRouteServiceAutocomplete'
import { ALL_OPERATION_DAYS } from 'models/services'
import { schema } from './schema'
import { InputCurrency } from 'components/InputCurrency'
import { BUSINESS_CLIENT_PRICE_VERTICALS } from 'features/BusinessClientPrice/utils'
import { updateDateRangeByEndDate, updateDateRangeByStartDate } from 'utils/date'
import { isPriceScheduleExistsError } from 'utils/error'

const messages = defineMessages({
  businessClientPlaceholder: {
    id: 'businessClientPrice.form.businessClient.placeholder',
    defaultMessage: 'Select business client'
  },
  businessSitePlaceholder: {
    id: 'businessClientPrice.form.businessSite.placeholder',
    defaultMessage: 'Select business site'
  },
  routePlaceholder: {
    id: 'businessClientPrice.form.route.placeholder',
    defaultMessage: 'Select Route'
  },
  routeServicesPlaceholder: {
    id: 'businessClientPrice.form.routeServices.placeholder',
    defaultMessage: 'Select route services'
  },
  endAtPlaceholder: {
    id: 'businessClientPrice.form.endDate.placeholder',
    defaultMessage: 'Select end At'
  },
  amountPlaceholder: {
    id: 'businessClientPrice.form.amount.placeholder',
    defaultMessage: '$0.00'
  }
})

const BusinessClientPriceCard = (): ReactElement => {
  const intl = useIntl()
  const { values, setFieldValue, setValues, touched, errors } = useFormikContext<INewBusinessClientPriceFormValues>()

  return (
    <FormLayout.Card small>
      <Row>
        <Column>
          <BusinessClientAutocomplete
            id="businessClient"
            value={values.businessClient?.name || ''}
            onChange={(businessClient): void => {
              setValues({
                ...values,
                businessClient: businessClient?.id ? businessClient : null,
                businessSite: null,
                routes: [],
                routeServices: []
              })
            }}
            placeholder={intl.formatMessage(messages.businessClientPlaceholder)}
            label={
              <FormattedMessage id="businessClientPrice.form.businessClient.label" defaultMessage="Business Client" />
            }
            optional
          />
        </Column>
        <Column>
          <BusinessSiteAutocomplete
            id="businessSite"
            label={<FormattedMessage id="businessClientPrice.form.businessSite.label" defaultMessage="Business Site" />}
            placeholder={intl.formatMessage(messages.businessSitePlaceholder)}
            value={values.businessSite?.name || ''}
            onChange={(businessSite): void => {
              setValues({
                ...values,
                businessSite: businessSite?.id ? businessSite : null,
                routes: [],
                routeServices: []
              })
            }}
            businessClientId={values.businessClient?.id}
            disabled={!values.businessClient?.id}
            optional
          />
        </Column>
      </Row>
      <Row>
        <Column width="50%">
          <RouteAutocomplete
            id="route"
            value={values.routes.at(0)?.name || ''}
            onChange={(routes): void => {
              setValues({
                ...values,
                routes,
                routeServices: []
              })
            }}
            placeholder={intl.formatMessage(messages.routePlaceholder)}
            label={<FormattedMessage id="businessClientPrice.form.route.label" defaultMessage="Route" />}
            verticals={BUSINESS_CLIENT_PRICE_VERTICALS}
            uniqueName
            businessClientIds={values.businessClient ? [values.businessClient.id] : []}
            businessSiteIds={values.businessSite ? [values.businessSite.id] : []}
            optional
          />
        </Column>
      </Row>
      <Row>
        <Column>
          <Label>
            <FormattedMessage id="businessClientPrice.form.services.label" defaultMessage="Services" />
          </Label>
          <MultiRouteServiceAutocomplete
            id="routeServices"
            values={values.routeServices}
            onChange={(routeServices): void => {
              setFieldValue('routeServices', routeServices)
            }}
            verticals={BUSINESS_CLIENT_PRICE_VERTICALS}
            routeIds={values.routes.map(({ id }): string => id)}
            placeholder={intl.formatMessage(messages.routeServicesPlaceholder)}
            touched={touched.routeServices}
            errors={errors.routeServices}
            isShowingAllRouteServicesOption={!isEmpty(values.routes)}
            uniqueCode
          />
        </Column>
      </Row>
      <Row>
        <Column>
          <InputDate
            intl={intl}
            id="startDate"
            value={values.startDate}
            onChange={(date): void => {
              const newDateRange = updateDateRangeByStartDate(
                {
                  startDate: values.startDate,
                  endDate: values.endDate
                },
                date as Date
              )
              setValues({
                ...values,
                startDate: newDateRange.startDate,
                endDate: newDateRange.endDate
              })
            }}
            label={<FormattedMessage id="businessClientPrice.form.startDate.label" defaultMessage="Start Date" />}
            disabledDays={{
              before: subMonths(new Date(), 3)
            }}
            touched={getIn(touched, 'startDate')}
            errors={getIn(errors, 'startDate')}
          />
        </Column>
        <Column>
          <InputDate
            intl={intl}
            id="endDate"
            value={values.endDate || ''}
            onChange={(date): void => {
              const newDateRange = updateDateRangeByEndDate(
                {
                  startDate: values.startDate,
                  endDate: values.endDate
                },
                date as Date
              )
              setValues({
                ...values,
                startDate: newDateRange.startDate,
                endDate: newDateRange.endDate
              })
            }}
            label={<FormattedMessage id="businessClientPrice.form.endDate.label" defaultMessage="End Date" />}
            placeholder={intl.formatMessage(messages.endAtPlaceholder)}
            disabledDays={{
              before: subMonths(new Date(), 3)
            }}
            touched={getIn(touched, 'endDate')}
            errors={getIn(errors, 'endDate')}
            optional
          />
        </Column>
        <Column>
          <DowSelector
            id="operationDays"
            values={values.operationDays}
            onChange={(operationDays: number[]): void => {
              setFieldValue('operationDays', operationDays)
            }}
            label={
              <FormattedMessage id="businessClientPrice.form.operationDays.label" defaultMessage="Operation Days" />
            }
            touched={getIn(touched, 'operationDays')}
            errors={getIn(errors, 'operationDays')}
          />
        </Column>
      </Row>
      <Row>
        <Column>
          <Label>
            <FormattedMessage id="businessClientPrice.form.amount.label" defaultMessage="Base Price per Trip" />
          </Label>
          <InputCurrency
            id="amount"
            value={values.amount || ''}
            onChange={(amount): void => {
              setFieldValue('amount', amount ? parseFloat(amount) : null)
            }}
            placeholder={intl.formatMessage(messages.amountPlaceholder)}
            touched={getIn(touched, 'amount')}
            errors={getIn(errors, 'amount')}
            hideSuffix
          />
        </Column>
      </Row>
    </FormLayout.Card>
  )
}

interface INewBusinessClientPriceProps {
  onSubmit: (
    values: INewBusinessClientPriceFormValues,
    helpers: FormikHelpers<INewBusinessClientPriceFormValues>
  ) => void
  loading?: boolean
  error?: ApolloError
}

export const NewBusinessClientPrice = ({ onSubmit, loading, error }: INewBusinessClientPriceProps): ReactElement => {
  const intl = useIntl()

  const initialValues: INewBusinessClientPriceFormValues = {
    businessClient: null,
    businessSite: null,
    routes: [],
    routeServices: [],
    startDate: new Date(),
    endDate: null,
    operationDays: ALL_OPERATION_DAYS,
    amount: null
  }

  return (
    <Formik initialValues={initialValues} validationSchema={schema(intl)} onSubmit={onSubmit}>
      {({ handleSubmit, dirty }): ReactElement => {
        return (
          <FormLayout.Content as="form" onSubmit={handleSubmit}>
            <Heading.H3>
              <FormattedMessage id="businessClientPrice.new.title" defaultMessage="Business Client Price" />
            </Heading.H3>
            <BusinessClientPriceCard />
            {error && (
              <FormLayout.Actions>
                {isPriceScheduleExistsError(error) ? (
                  <ErrorMessage>
                    <FormattedMessage
                      id="businessClientPrice.error.priceScheduleExists"
                      defaultMessage="The service has a record with the same price and date range."
                    />
                  </ErrorMessage>
                ) : (
                  <ErrorMessage errors={getUnprocessableErrors(error)}>
                    <FormattedMessage
                      id="businessClientPrice.error"
                      defaultMessage="There was an error while creating your business client price."
                    />
                  </ErrorMessage>
                )}
              </FormLayout.Actions>
            )}
            <FormLayout.Actions>
              <Button type="submit" kind="primary" disabled={!dirty} loading={loading}>
                <FormattedMessage id="dictionary.save" defaultMessage="Save" />
              </Button>
            </FormLayout.Actions>
          </FormLayout.Content>
        )
      }}
    </Formik>
  )
}
